import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import {
  Calendar,
  Clock,
  User,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Users,
  Plus,
  Trash2,
  Lock,
  Shield,
} from "lucide-react";
import { API } from "@/App";
import { format, addDays, startOfWeek, isSameDay, isToday, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale , toLocalDateString, todayLocalString} from "@/utils/dateUtils";
import TimePicker from "./TimePicker";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function MyClassesModule({ user, selectedBusiness }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "employees"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [trainerId, setTrainerId] = useState(null);
  const [trainerInfo, setTrainerInfo] = useState(null);
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [stats, setStats] = useState({
    today: 0,
    week: 0,
    completed: 0,
    cancelled: 0,
  });
  
  // Time Block Modal States
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockForm, setBlockForm] = useState({
    date: "",
    start_time: "",
    end_time: "",
    reason: "personal",
    notes: "",
  });
  const [deleteBlockId, setDeleteBlockId] = useState(null);
  const [savingBlock, setSavingBlock] = useState(false);
  
  // Check if trainer is manager at selected business
  const isManager = selectedBusiness?.is_manager || selectedBusiness?.is_own_business;

  // Generate week days
  const getWeekDays = () => {
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  useEffect(() => {
    if (user?.user_id) {
      fetchTrainerAndClasses();
    }
  }, [user, selectedBusiness]);

  const fetchTrainerAndClasses = async () => {
    try {
      // Get trainer info
      const trainerRes = await fetch(`${API}/trainers/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (trainerRes.ok) {
        const trainers = await trainerRes.json();
        if (trainers.length > 0) {
          // If business is selected, find the trainer for that business
          const businessId = selectedBusiness?.business_id;
          const trainer = businessId 
            ? trainers.find(t => t.business_id === businessId) || trainers[0]
            : trainers[0];
          
          setTrainerId(trainer.trainer_id);
          setTrainerInfo(trainer);
          
          // Get reservations for this trainer
          const resRes = await fetch(
            `${API}/businesses/${trainer.business_id}/reservations?trainer_id=${trainer.trainer_id}`,
            { credentials: "include" }
          );
          
          if (resRes.ok) {
            const reservations = await resRes.json();
            setReservations(reservations);
            calculateStats(reservations);
          }
          
          // Get time blocks
          fetchTimeBlocks(trainer.trainer_id, trainer.business_id);
        }
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTimeBlocks = async (tId, bId) => {
    try {
      const response = await fetch(
        `${API}/trainers/${tId}/time-blocks?business_id=${bId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const blocks = await response.json();
        setTimeBlocks(blocks);
      }
    } catch (error) {
      console.error("Error fetching time blocks:", error);
    }
  };

  const calculateStats = (reservations) => {
    const today = format(new Date(), "yyyy-MM-dd");
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 6);
    
    setStats({
      today: reservations.filter((r) => r.date === today && ["pending", "confirmed"].includes(r.status)).length,
      week: reservations.filter((r) => {
        const date = parseISO(r.date);
        return date >= weekStart && date <= weekEnd && ["pending", "confirmed"].includes(r.status);
      }).length,
      completed: reservations.filter((r) => r.status === "completed").length,
      cancelled: reservations.filter((r) => r.status === "cancelled").length,
    });
  };

  const getDayReservations = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return reservations
      .filter((r) => r.date === dateStr)
      .sort((a, b) => {
        const timeA = a.start_time || a.time || "00:00";
        const timeB = b.start_time || b.time || "00:00";
        return timeA.localeCompare(timeB);
      });
  };
  
  // Get time blocks for a specific date
  const getDayTimeBlocks = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return timeBlocks.filter((b) => b.date === dateStr);
  };
  
  // Create time block
  const handleCreateTimeBlock = async () => {
    if (!blockForm.date || !blockForm.start_time || !blockForm.end_time) {
      toast.error(t('common:fillAllFields'));
      return;
    }
    
    setSavingBlock(true);
    try {
      const response = await fetch(`${API}/trainers/${trainerId}/time-blocks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...blockForm,
          business_id: trainerInfo?.business_id,
        }),
      });
      
      if (response.ok) {
        toast.success(t("common:toast_zamanEngeliOlusturuldu"));
        setShowBlockModal(false);
        setBlockForm({ date: "", start_time: "", end_time: "", reason: "personal", notes: "" });
        fetchTimeBlocks(trainerId, trainerInfo?.business_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:errorOccurred"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingBlock(false);
    }
  };
  
  // Delete time block
  const handleDeleteTimeBlock = async () => {
    if (!deleteBlockId) return;
    
    try {
      const response = await fetch(`${API}/trainers/${trainerId}/time-blocks/${deleteBlockId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Zaman engeli silindi");
        setDeleteBlockId(null);
        fetchTimeBlocks(trainerId, trainerInfo?.business_id);
      } else {
        toast.error("Silinemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  // Get reason label
  const getReasonLabel = (reason) => {
    const reasons = {
      personal: t("common:personal"),
      sick: t("common:sick"),
      vacation: t("common:vacation"),
      meeting: t("admin:meetingLabel"),
      other: t("common:other"),
    };
    return reasons[reason] || reason;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return <Badge className="bg-green-100 text-green-600"><CheckCircle className="w-3 h-3 mr-1" />{t("admin:approved")}</Badge>;
      case "pending":
        return <Badge className="bg-yellow-100 text-yellow-600"><AlertCircle className="w-3 h-3 mr-1" />{t("trainers:pendingStatus")}</Badge>;
      case "completed":
        return <Badge className="bg-blue-100 text-blue-600"><CheckCircle className="w-3 h-3 mr-1" />{t('common:completed')}</Badge>;
      case "cancelled":
        return <Badge className="bg-red-100 text-red-600"><XCircle className="w-3 h-3 mr-1" />{t('common:cancel')}</Badge>;
      case "burned":
        return <Badge className="bg-gray-100 text-gray-600"><XCircle className="w-3 h-3 mr-1" />{t("reservations:absent")}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTrainingTypeLabel = (type) => {
    // Prefer dynamic (admin_settings) translation when available; fall back to
    // static i18n labels so trainers see a translated label even when the
    // business hasn't configured custom training types.
    const legacyMap = {
      one_on_one: "Birebir",
      duet: "Düet",
      group: "Grup",
    };
    const canonicalName = legacyMap[type] || type;
    const localized = getLocalizedTrainingType(canonicalName);
    // If LocalizedData returned the same Turkish fallback, use static i18n
    if (localized === canonicalName) {
      const staticKey = {
        one_on_one: "reservations:oneOnOneLabel",
        duet: "reservations:duet",
        group: "reservations:group",
      }[type];
      if (staticKey) return t(staticKey);
    }
    return localized;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t("trainers:myClassesTitle")}</h2>
          <p className="text-grey text-sm">{t("common:dersPrograminiziGoruntuleyin")}</p>
        </div>
        
        {/* Time Block Button - Only for Managers */}
        {isManager ? (
          <Button
            onClick={() => {
              setBlockForm({
                date: format(selectedDate, "yyyy-MM-dd"),
                start_time: "",
                end_time: "",
                reason: "personal",
                notes: "",
              });
              setShowBlockModal(true);
            }}
            className="bg-amber-500 hover:bg-amber-600 rounded-xl"
          >
            <Lock className="w-4 h-4 mr-2" />
            {t("trainers:createTimeBlock")}
          </Button>
        ) : (
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-xl text-sm text-grey">
            <Shield className="w-4 h-4" />
            <span>{t("employees:zamanEngellemekIcinYoneticiOlmalisiniz")}</span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.today}</p>
                <p className="text-xs text-grey">{t('common:today')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.week}</p>
                <p className="text-xs text-grey">{t('common:thisWeek')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.completed}</p>
                <p className="text-xs text-grey">{t("trainers:completedCount")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.cancelled}</p>
                <p className="text-xs text-grey">{t('common:cancel')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Week Navigation */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedDate(addDays(selectedDate, -7))}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="font-medium text-navy">
              {format(getWeekDays()[0], "dd/MM", { locale: getDateLocale() })} - {format(getWeekDays()[6], "dd/MM/yyyy", { locale: getDateLocale() })}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedDate(addDays(selectedDate, 7))}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="grid grid-cols-7 gap-2">
            {getWeekDays().map((day) => {
              const dayReservations = getDayReservations(day);
              const isSelected = isSameDay(day, selectedDate);
              
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  className={`p-2 rounded-xl text-center transition-all ${
                    isSelected
                      ? "bg-navy text-white"
                      : isToday(day)
                      ? "bg-red-50 text-red-600 border-2 border-red-200"
                      : "bg-slate-50 hover:bg-slate-100"
                  }`}
                >
                  <span className="text-xs font-medium uppercase block">
                    {format(day, "EEE", { locale: getDateLocale() })}
                  </span>
                  <span className="text-lg font-bold block">{format(day, "d")}</span>
                  {dayReservations.length > 0 && (
                    <span className={`text-xs ${
                      isSelected ? "text-white/80" : "text-grey"
                    }`}>
                      {dayReservations.length} {t("common:lessonsCount")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Day Schedule */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">
            {format(selectedDate, "dd/MM/yyyy, EEEE", { locale: getDateLocale() })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Time Blocks for this day */}
          {getDayTimeBlocks(selectedDate).length > 0 && (
            <div className="mb-4 space-y-2">
              <p className="text-sm font-medium text-amber-600 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Zaman Engelleri
              </p>
              {getDayTimeBlocks(selectedDate).map((block) => (
                <div
                  key={block.block_id}
                  className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <p className="font-medium text-amber-700">{block.start_time} - {block.end_time}</p>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">
                      {getReasonLabel(block.reason)}
                    </Badge>
                    {block.notes && <span className="text-sm text-grey">{block.notes}</span>}
                  </div>
                  {isManager && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteBlockId(block.block_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Reservations */}
          {getDayReservations(selectedDate).length > 0 ? (
            <div className="space-y-3">
              {getDayReservations(selectedDate).map((res) => (
                <div
                  key={res.reservation_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-navy">{res.start_time || res.time}</p>
                    </div>
                    <div className="w-px h-10 bg-gray-200" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{res.member_name || `${t("trainers:memberShort")} #${res.member_id?.slice(-6)}`}</p>
                        {getStatusBadge(res.status)}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-grey mt-1">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {getTrainingTypeLabel(res.training_type)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {getLocalizedBranch(res.branch)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : getDayTimeBlocks(selectedDate).length === 0 ? (
            <div className="text-center py-8 text-grey">
              <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>{t("common:buGunIcinDersBulunmuyor")}</p>
            </div>
          ) : null}
        </CardContent>
      </Card>
      
      {/* Time Block Creation Modal */}
      <Dialog open={showBlockModal} onOpenChange={setShowBlockModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" />
              {t("trainers:createTimeBlock")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t("common:dateRequired")}</Label>
              <DatePickerTR
                value={blockForm.date ? new Date(blockForm.date) : null}
                onChange={(date) => setBlockForm({ ...blockForm, date: date ? toLocalDateString(date) : "" })}
                placeholder={t('common:selectDate')}
                minDate={new Date()}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("trainers:startTimeLabel")}</Label>
                <TimePicker
                  value={blockForm.start_time}
                  onChange={(val) => setBlockForm({ ...blockForm, start_time: val })}
                  placeholder={t('settings:startTime')}
                  minHour={6}
                  maxHour={22}
                  minuteStep={15}
                />
              </div>
              <div>
                <Label>{t("trainers:endTimeLabel")}</Label>
                <TimePicker
                  value={blockForm.end_time}
                  onChange={(val) => setBlockForm({ ...blockForm, end_time: val })}
                  placeholder={t('settings:endTime')}
                  minHour={6}
                  maxHour={23}
                  minuteStep={15}
                />
              </div>
            </div>
            
            <div>
              <Label>{t("common:reason")}</Label>
              <Select
                value={blockForm.reason}
                onValueChange={(val) => setBlockForm({ ...blockForm, reason: val })}
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">{t("common:kisisel")}</SelectItem>
                  <SelectItem value="sick">{t("common:hastalik")}</SelectItem>
                  <SelectItem value="vacation">{t("common:i̇zin")}</SelectItem>
                  <SelectItem value="meeting">{t("admin:toplanti")}</SelectItem>
                  <SelectItem value="other">{t('common:other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>{t("trainers:noteOptional")}</Label>
              <Input
                value={blockForm.notes}
                onChange={(e) => setBlockForm({ ...blockForm, notes: e.target.value })}
                className="rounded-xl mt-1"
                placeholder={t("common:additionalInfo")}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowBlockModal(false)} className="rounded-xl">
              {t("common:cancel")}
            </Button>
            <Button
              onClick={handleCreateTimeBlock}
              disabled={savingBlock}
              className="bg-amber-500 hover:bg-amber-600 rounded-xl"
            >
              {savingBlock ? t("common:saving") : t("common:createBlock")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Time Block Confirmation */}
      <AlertDialog open={!!deleteBlockId} onOpenChange={(open) => !open && setDeleteBlockId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('reservations:deleteTimeBlock')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("trainers:confirmDeleteTimeBlock")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTimeBlock}
              className="bg-red-500 hover:bg-red-600 rounded-xl"
            >
              {t("common:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
